import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';

// Types
export interface ModifierOption {
  id: string;
  modifier_group_id: string;
  name: string;
  price_adjustment: number;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ModifierGroup {
  id: string;
  location_id: string;
  name: string;
  min_selections: number;
  max_selections: number;
  is_required: boolean;
  created_at: string;
  updated_at: string;
  options?: ModifierOption[];
}

export interface ProductModifierGroup {
  id: string;
  product_id: string;
  modifier_group_id: string;
  sort_order: number;
  modifier_group?: ModifierGroup;
}

export interface SelectedModifier {
  id: string;
  name: string;
  price: number;
  group_id: string;
  group_name: string;
}

// Hook for managing modifier groups at location level
export function useModifierGroups() {
  const queryClient = useQueryClient();
  const { currentLocation } = useLocationStore();
  const locationId = currentLocation?.id;

  // Fetch all modifier groups for the location
  const {
    data: modifierGroups,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['modifier-groups', locationId],
    queryFn: async () => {
      if (!locationId) return [];

      const { data, error } = await supabase
        .from('modifier_groups')
        .select(`
          *,
          options:modifier_options(*)
        `)
        .eq('location_id', locationId)
        .order('name');

      if (error) throw error;

      // Sort options within each group by sort_order
      return (data as ModifierGroup[]).map(group => ({
        ...group,
        options: group.options?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
      }));
    },
    enabled: !!locationId,
  });

  // Create a new modifier group
  const createModifierGroup = useMutation({
    mutationFn: async ({
      name,
      minSelections = 0,
      maxSelections = 1,
      isRequired = false,
      options = [],
    }: {
      name: string;
      minSelections?: number;
      maxSelections?: number;
      isRequired?: boolean;
      options?: { name: string; priceAdjustment: number; isDefault?: boolean }[];
    }) => {
      if (!locationId) throw new Error('No location selected');

      // Create the modifier group
      const { data: group, error: groupError } = await supabase
        .from('modifier_groups')
        .insert({
          location_id: locationId,
          name,
          min_selections: minSelections,
          max_selections: maxSelections,
          is_required: isRequired,
        })
        .select()
        .single();

      if (groupError) throw groupError;

      // Create options if provided
      if (options.length > 0) {
        const { error: optionsError } = await supabase
          .from('modifier_options')
          .insert(
            options.map((opt, index) => ({
              modifier_group_id: group.id,
              name: opt.name,
              price_adjustment: opt.priceAdjustment,
              is_default: opt.isDefault || false,
              is_active: true,
              sort_order: index,
            }))
          );

        if (optionsError) throw optionsError;
      }

      return group;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifier-groups', locationId] });
    },
  });

  // Update a modifier group
  const updateModifierGroup = useMutation({
    mutationFn: async ({
      id,
      name,
      minSelections,
      maxSelections,
      isRequired,
    }: {
      id: string;
      name?: string;
      minSelections?: number;
      maxSelections?: number;
      isRequired?: boolean;
    }) => {
      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (name !== undefined) updates.name = name;
      if (minSelections !== undefined) updates.min_selections = minSelections;
      if (maxSelections !== undefined) updates.max_selections = maxSelections;
      if (isRequired !== undefined) updates.is_required = isRequired;

      const { data, error } = await supabase
        .from('modifier_groups')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifier-groups', locationId] });
    },
  });

  // Delete a modifier group
  const deleteModifierGroup = useMutation({
    mutationFn: async (id: string) => {
      // First delete all options
      await supabase
        .from('modifier_options')
        .delete()
        .eq('modifier_group_id', id);

      // Then delete the group
      const { error } = await supabase
        .from('modifier_groups')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifier-groups', locationId] });
    },
  });

  // Add an option to a modifier group
  const addModifierOption = useMutation({
    mutationFn: async ({
      groupId,
      name,
      priceAdjustment = 0,
      isDefault = false,
    }: {
      groupId: string;
      name: string;
      priceAdjustment?: number;
      isDefault?: boolean;
    }) => {
      // Get current max sort order
      const group = modifierGroups?.find(g => g.id === groupId);
      const maxSortOrder = group?.options?.length || 0;

      const { data, error } = await supabase
        .from('modifier_options')
        .insert({
          modifier_group_id: groupId,
          name,
          price_adjustment: priceAdjustment,
          is_default: isDefault,
          is_active: true,
          sort_order: maxSortOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifier-groups', locationId] });
    },
  });

  // Update a modifier option
  const updateModifierOption = useMutation({
    mutationFn: async ({
      id,
      name,
      priceAdjustment,
      isDefault,
      isActive,
      sortOrder,
    }: {
      id: string;
      name?: string;
      priceAdjustment?: number;
      isDefault?: boolean;
      isActive?: boolean;
      sortOrder?: number;
    }) => {
      const updates: Record<string, unknown> = { updated_at: new Date().toISOString() };
      if (name !== undefined) updates.name = name;
      if (priceAdjustment !== undefined) updates.price_adjustment = priceAdjustment;
      if (isDefault !== undefined) updates.is_default = isDefault;
      if (isActive !== undefined) updates.is_active = isActive;
      if (sortOrder !== undefined) updates.sort_order = sortOrder;

      const { data, error } = await supabase
        .from('modifier_options')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifier-groups', locationId] });
    },
  });

  // Delete a modifier option
  const deleteModifierOption = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('modifier_options')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modifier-groups', locationId] });
    },
  });

  return {
    modifierGroups: modifierGroups ?? [],
    isLoading,
    error,
    refetch,
    createModifierGroup,
    updateModifierGroup,
    deleteModifierGroup,
    addModifierOption,
    updateModifierOption,
    deleteModifierOption,
  };
}

// Hook for managing product-specific modifiers
export function useProductModifiers(productId: string | null) {
  const queryClient = useQueryClient();

  // Fetch modifier groups assigned to this product
  const {
    data: productModifiers,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['product-modifiers', productId],
    queryFn: async () => {
      if (!productId) return [];

      const { data, error } = await supabase
        .from('product_modifier_groups')
        .select(`
          *,
          modifier_group:modifier_groups(
            *,
            options:modifier_options(*)
          )
        `)
        .eq('product_id', productId)
        .order('sort_order');

      if (error) throw error;

      // Sort options within each group and filter active only
      return (data as ProductModifierGroup[]).map(pmg => ({
        ...pmg,
        modifier_group: pmg.modifier_group ? {
          ...pmg.modifier_group,
          options: pmg.modifier_group.options
            ?.filter(opt => opt.is_active)
            ?.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
        } : undefined,
      }));
    },
    enabled: !!productId,
  });

  // Check if product has any modifiers
  const hasModifiers = (productModifiers?.length ?? 0) > 0;

  // Check if product has required modifiers
  const hasRequiredModifiers = productModifiers?.some(
    pmg => pmg.modifier_group?.is_required
  ) ?? false;

  // Get default selections for a product
  const getDefaultSelections = useCallback((): SelectedModifier[] => {
    if (!productModifiers) return [];

    const defaults: SelectedModifier[] = [];
    for (const pmg of productModifiers) {
      const group = pmg.modifier_group;
      if (!group?.options) continue;

      for (const option of group.options) {
        if (option.is_default) {
          defaults.push({
            id: option.id,
            name: option.name,
            price: option.price_adjustment || 0,
            group_id: group.id,
            group_name: group.name,
          });
        }
      }
    }
    return defaults;
  }, [productModifiers]);

  // Assign modifier groups to a product
  const setProductModifiers = useMutation({
    mutationFn: async ({
      productId,
      modifierGroupIds,
    }: {
      productId: string;
      modifierGroupIds: string[];
    }) => {
      // Delete existing assignments
      await supabase
        .from('product_modifier_groups')
        .delete()
        .eq('product_id', productId);

      // Insert new assignments
      if (modifierGroupIds.length > 0) {
        const { error } = await supabase
          .from('product_modifier_groups')
          .insert(
            modifierGroupIds.map((groupId, index) => ({
              product_id: productId,
              modifier_group_id: groupId,
              sort_order: index,
            }))
          );

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-modifiers', productId] });
    },
  });

  // Add a single modifier group to a product
  const addModifierToProduct = useMutation({
    mutationFn: async ({ modifierGroupId }: { modifierGroupId: string }) => {
      if (!productId) throw new Error('No product ID');

      const currentCount = productModifiers?.length || 0;

      const { data, error } = await supabase
        .from('product_modifier_groups')
        .insert({
          product_id: productId,
          modifier_group_id: modifierGroupId,
          sort_order: currentCount,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-modifiers', productId] });
    },
  });

  // Remove a modifier group from a product
  const removeModifierFromProduct = useMutation({
    mutationFn: async ({ modifierGroupId }: { modifierGroupId: string }) => {
      if (!productId) throw new Error('No product ID');

      const { error } = await supabase
        .from('product_modifier_groups')
        .delete()
        .eq('product_id', productId)
        .eq('modifier_group_id', modifierGroupId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-modifiers', productId] });
    },
  });

  // Validate selections against group requirements
  const validateSelections = useCallback((
    selections: SelectedModifier[]
  ): { valid: boolean; errors: string[] } => {
    if (!productModifiers) return { valid: true, errors: [] };

    const errors: string[] = [];

    for (const pmg of productModifiers) {
      const group = pmg.modifier_group;
      if (!group) continue;

      const groupSelections = selections.filter(s => s.group_id === group.id);
      const count = groupSelections.length;

      if (group.is_required && count === 0) {
        errors.push(`"${group.name}" is required`);
      }

      if (group.min_selections && count < group.min_selections) {
        errors.push(`Select at least ${group.min_selections} option(s) for "${group.name}"`);
      }

      if (group.max_selections && count > group.max_selections) {
        errors.push(`Select at most ${group.max_selections} option(s) for "${group.name}"`);
      }
    }

    return { valid: errors.length === 0, errors };
  }, [productModifiers]);

  return {
    productModifiers: productModifiers ?? [],
    hasModifiers,
    hasRequiredModifiers,
    isLoading,
    error,
    refetch,
    getDefaultSelections,
    setProductModifiers,
    addModifierToProduct,
    removeModifierFromProduct,
    validateSelections,
  };
}

// Simple hook to check if a product has modifiers (for POS quick check)
export function useProductHasModifiers(productId: string | null) {
  const { data, isLoading } = useQuery({
    queryKey: ['product-has-modifiers', productId],
    queryFn: async () => {
      if (!productId) return false;

      const { count, error } = await supabase
        .from('product_modifier_groups')
        .select('id', { count: 'exact', head: true })
        .eq('product_id', productId);

      if (error) throw error;
      return (count ?? 0) > 0;
    },
    enabled: !!productId,
  });

  return { hasModifiers: data ?? false, isLoading };
}
